/* eslint-disable compat/compat */
// eslint-disable-next-line import/no-unresolved
import KeenSlider from 'https://cdn.skypack.dev/pin/keen-slider@v6.7.0-LKl0HtBlpKLwpmTQiIJc/mode=imports,min/optimized/keen-slider.js'

const { getGenderFromFilename } = window.BAO.Utils

class ProductMediaCarousel extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
    if (window.innerWidth < 768) {
      this.els.slide.elements[0]
        .querySelector('img')
        ?.removeAttribute('loading')
    }

    this.els.slides.element.classList.add('keen-slider')
    this.els.slide.elements.forEach(el =>
      el.classList.add('keen-slider__slide'),
    )

    const url = new URL(window.location)
    const gender =
      (url.searchParams.has('gender') && url.searchParams.get('gender')) ||
      sessionStorage.getItem('gender') ||
      'female'
    this.setGenderBasedMedia(gender)
    this.setGenderCartAttribute(gender)

    this.carousel = this.initialiseCarousel()

    const zoomableSlides = this.els.slide.elements.filter(
      slide => slide.dataset.zoom !== 'false',
    )

    zoomableSlides.forEach(slide => {
      this.zoom(slide)
    })
  }

  setupListeners () {
    super.setupListeners()
    this.listeners.add(
      document,
      'variantChange',
      this.handleVariantChange.bind(this),
    )
  }

  handleVariantChange () {
    window.setTimeout(() => {
      this.carousel = this.initialiseCarousel()
    }, 10)
  }

  useDots = (dots = []) => {
    /** @param {KeenSliderInstance} carousel */
    return carousel => {
      const updateDotStates = () => {
        dots.forEach((dot, index) => {
          const isCurrent = carousel.track.details.rel === index

          dot.setAttribute('aria-current', String(isCurrent))
        })
      }

      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          carousel.moveToIdx(index)
        })
      })

      carousel.on('created', updateDotStates)
      carousel.on('slideChanged', updateDotStates)
    }
  }

  setGenderBasedMedia (gender) {
    if (!this.unisexProduct) return

    if (!['male', 'female'].includes(gender)) {
      return
    }

    const oppositeGender = gender === 'male' ? 'female' : 'male'
    const slides = this.els.slide.elements

    // Remove opposite gender slides, keeping original order
    slides.forEach(slide => {
      const el = slide.querySelector('img, lazy-video')

      if (!el) return
      const src =
        el.tagName === 'IMG' ? el.src : el.querySelector('source')?.dataset?.src
      if (getGenderFromFilename(src) === oppositeGender) {
        slide.remove()
      }
    })
  }

  setGenderCartAttribute (gender) {
    sessionStorage.setItem('gender', gender)
    window.BAO.Cart.updateCartAttributes({ gender })
  }

  initialiseCarousel () {
    return new KeenSlider(this.els.slides.element, {}, [
      this.useDots(this.els.dots.elements),
      carousel => {
        const playAllVideoSlides = instance => {
          /** @type {HTMLVideoElement[]} */
          const videos = instance.slides
            .map(slide => slide.querySelector('video'))
            .filter(Boolean)

          Promise.all(
            videos.map(async video => {
              if (!video.paused) {
                return
              }

              return video.play()
            }),
          ).finally(() => {
            carousel.on('dragStarted', playAllVideoSlides, true)
          })
        }

        carousel.on('dragStarted', playAllVideoSlides)
        carousel.on('updated', () => {
          carousel.on('dragStarted', playAllVideoSlides)
        })
      },
    ])
  }

  zoom (slide) {
    const sliderElement = slide
    let imageElementScale = 1

    const start = {}

    // Calculate distance between two fingers
    const distance = event => {
      return Math.hypot(
        event.touches[0].pageX - event.touches[1].pageX,
        event.touches[0].pageY - event.touches[1].pageY,
      )
    }

    sliderElement.addEventListener('touchstart', event => {
      const imageContainer = event.target
      if (event.touches.length === 2) {
        event.preventDefault() // Prevent page scroll

        event.target
          .closest('[data-product-media-carousel-el="slide"]')
          .classList.add('util-DisableTransform')
        imageContainer.parentElement.parentElement.classList.add(
          'prd-Detail_Image-active',
        )
        imageContainer.style.position = 'absolute'
        imageContainer.style.width = '100%'

        // Calculate where the fingers have started on the X and Y axis
        start.x = (event.touches[0].pageX + event.touches[1].pageX) / 2
        start.y = (event.touches[0].pageY + event.touches[1].pageY) / 2
        start.distance = distance(event)
      }
    })

    sliderElement.addEventListener('touchmove', event => {
      const imageContainer = event.target
      if (event.touches.length === 2) {
        event.preventDefault() // Prevent page scroll
        let scale

        // Safari provides event.scale as two fingers move on the screen
        // For other browsers just calculate the scale manually
        if (event.scale) {
          scale = event.scale
        } else {
          const deltaDistance = distance(event)
          scale = deltaDistance / start.distance
        }
        const zoomLevel = Number(
          event.target.parentElement.parentElement.dataset.zoomLevel,
        )
        imageElementScale = Math.min(Math.max(1, scale), zoomLevel)

        // Calculate how much the fingers have moved on the X and Y axis
        const deltaX =
          ((event.touches[0].pageX + event.touches[1].pageX) / 2 - start.x) * 2 // x2 for accelarated movement
        const deltaY =
          ((event.touches[0].pageY + event.touches[1].pageY) / 2 - start.y) * 2 // x2 for accelarated movement

        // Transform the image to make it grow and move with fingers
        const transform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${imageElementScale})`
        imageContainer.style.transform = transform
        imageContainer.style.WebkitTransform = transform
        imageContainer.style.zIndex = '9999'
      }
    })

    sliderElement.addEventListener('touchend', event => {
      const imageContainer = event.target
      event.target
        .closest('[data-product-media-carousel-el="slide"]')
        .classList.remove('util-DisableTransform')
      imageContainer.parentElement.parentElement.classList.remove(
        'prd-Detail_Image-active',
      )
      // Reset image to it's original format
      imageContainer.style.transform = ''
      imageContainer.style.WebkitTransform = ''
      imageContainer.style.zIndex = ''
    })
  }

  get unisexProduct () {
    const { productGender } = this.dataset
    return productGender === 'unisex'
  }
}

if (!customElements.get('')) {
  customElements.define('product-media-carousel', ProductMediaCarousel)
}
